import React, { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@apollo/client";
import {
  Box,
  Heading,
  VStack,
  HStack,
  Text,
  Button,
  Card,
  CardHeader,
  CardBody,
  Alert,
  AlertIcon,
  Icon,
  Divider,
  SimpleGrid,
  Badge,
  useToast,
  useColorMode,
  useColorModeValue,
} from "@chakra-ui/react";
import { CheckCircleIcon, DownloadIcon, ArrowBackIcon } from "@chakra-ui/icons";
import { usePageTitle } from "../../hooks/useDocumentTitle";
import { brandConfig, lightTheme, darkTheme } from "../../brandConfig";
import * as confettiLib from "canvas-confetti";
import { PoweredBy } from "./components/PoweredBy";
import { PUBLIC_GET_BILL, PUBLIC_GET_CLIENT_BY_BILL } from "./queries";

const confetti = confettiLib.default || confettiLib;

const BillPaymentSuccess = () => {
  usePageTitle("Payment Success");
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { colorMode } = useColorMode();

  // Brand colors (module pattern — see vault/how-to/color-mode-pattern.md)
  // useColorModeValue is Chakra's official hook — re-renders on toggle, reads
  // straight from lightTheme/darkTheme objects (no getColor middleman).
  const cardGradientBg = useColorModeValue(lightTheme.background.cardGradient, darkTheme.background.cardGradient);
  const cardBorder = useColorModeValue(lightTheme.border.darkCard, darkTheme.border.darkCard);
  const textPrimary = useColorModeValue(lightTheme.text.primary, darkTheme.text.primary);
  const textSecondary = useColorModeValue(lightTheme.text.secondary, darkTheme.text.secondary);
  // Status colors live on brandConfig.colors only (not split per theme — same green works in both modes)
  const statusSuccess = brandConfig.colors.status.success;

  const { data: billData, loading: billLoading } = useQuery(PUBLIC_GET_BILL, {
    variables: { id },
  });

  const { data: clientData, loading: clientLoading } = useQuery(PUBLIC_GET_CLIENT_BY_BILL, {
    variables: { billId: id },
  });

  // Trigger confetti animation on mount
  useEffect(() => {
    const duration = 3 * 1000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

    function randomInRange(min: number, max: number) {
      return Math.random() * (max - min) + min;
    }

    const interval: any = setInterval(function() {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);
      
      confetti(Object.assign({}, defaults, {
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      }));
      confetti(Object.assign({}, defaults, {
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      }));
    }, 250);

    return () => clearInterval(interval);
  }, []);

  const bill = billData?.publicBill;
  const client = clientData?.publicGetClientDetailsByBillId;

  const getDisplayBillId = (fullId: string) => {
    return fullId.substring(0, 4);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
      <VStack spacing={8} align="stretch">
          {/* Success Header */}
          <VStack spacing={4} textAlign="center">
            <Icon as={CheckCircleIcon} w={20} h={20} color={statusSuccess} />
            <Heading size="2xl" color={textPrimary}>
              Payment Successful!
            </Heading>
            <Text fontSize="lg" color={textSecondary}>
              Thank you for your payment. Your transaction has been completed successfully.
            </Text>
          </VStack>

          {/* Payment Details Card */}
          <Card
            bg={cardGradientBg}
            borderColor={cardBorder}
            borderWidth="1px"
            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
            backdropFilter="blur(10px)"
          >
            <CardHeader>
              <HStack justify="space-between">
                <Heading size="md" color={textPrimary}>
                  Payment Receipt
                </Heading>
                <Badge colorScheme="green" fontSize="lg" px={3} py={1}>
                  PAID
                </Badge>
              </HStack>
            </CardHeader>

            <CardBody>
              <VStack spacing={6} align="stretch">
                {/* Transaction Summary */}
                <Box>
                  <Text fontWeight="bold" fontSize="sm" color={textSecondary} mb={2}>
                    TRANSACTION SUMMARY
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <Box>
                      <Text fontSize="sm" color={textSecondary}>Invoice Number</Text>
                      <Text fontSize="lg" fontWeight="bold" color={textPrimary}>
                        #{bill ? getDisplayBillId(bill.id) : "..."}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" color={textSecondary}>Amount Paid</Text>
                      <Text fontSize="lg" fontWeight="bold" color={statusSuccess}>
                        ${bill?.totalAmount?.toFixed(2) || "0.00"} {bill?.currency || "AUD"}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" color={textSecondary}>Payment Date</Text>
                      <Text fontSize="md" color={textPrimary}>
                        {formatDate(new Date().toISOString())}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" color={textSecondary}>Payment Method</Text>
                      <Text fontSize="md" color={textPrimary}>
                        Credit/Debit Card (Stripe)
                      </Text>
                    </Box>
                  </SimpleGrid>
                </Box>

                <Divider />

                {/* Customer Information */}
                {client && (
                  <>
                    <Box>
                      <Text fontWeight="bold" fontSize="sm" color={textSecondary} mb={2}>
                        CUSTOMER INFORMATION
                      </Text>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                        <Box>
                          <Text fontSize="sm" color={textSecondary}>Name</Text>
                          <Text fontSize="md" color={textPrimary}>
                            {client.fName} {client.lName}
                          </Text>
                        </Box>
                        <Box>
                          <Text fontSize="sm" color={textSecondary}>Email</Text>
                          <Text fontSize="md" color={textPrimary}>
                            {client.email}
                          </Text>
                        </Box>
                        {client.businessName && (
                          <Box>
                            <Text fontSize="sm" color={textSecondary}>Business</Text>
                            <Text fontSize="md" color={textPrimary}>
                              {client.businessName}
                            </Text>
                          </Box>
                        )}
                      </SimpleGrid>
                    </Box>
                    <Divider />
                  </>
                )}

                {/* Line Items */}
                {bill?.lineItems && bill.lineItems.length > 0 && (
                  <Box>
                    <Text fontWeight="bold" fontSize="sm" color={textSecondary} mb={2}>
                      ITEMS PAID
                    </Text>
                    <VStack spacing={2} align="stretch">
                      {bill.lineItems.map((item: any) => (
                        <HStack key={item.id} justify="space-between" p={2}>
                          <Text color={textPrimary}>{item.description}</Text>
                          <Text fontWeight="bold" color={textPrimary}>
                            ${item.amount.toFixed(2)}
                          </Text>
                        </HStack>
                      ))}
                      <Divider />
                      <HStack justify="space-between" p={2}>
                        <Text fontWeight="bold" color={textPrimary}>
                          Total Paid
                        </Text>
                        <Text fontWeight="bold" fontSize="lg" color={statusSuccess}>
                          ${bill.totalAmount?.toFixed(2) || "0.00"} {bill?.currency || "AUD"}
                        </Text>
                      </HStack>
                    </VStack>
                  </Box>
                )}

                {/* Confirmation Notice */}
                <Alert status="success" borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <Text fontWeight="bold">Payment Confirmed</Text>
                    <Text fontSize="sm">
                      A confirmation email has been sent to your registered email address with the payment details.
                    </Text>
                  </Box>
                </Alert>
              </VStack>
            </CardBody>
          </Card>

          {/* Action Buttons */}
          <HStack spacing={4} justify="center">
            <Button
              leftIcon={<ArrowBackIcon />}
              variant="outline"
              onClick={() => navigate(`/bill/${id}`)}
              colorScheme="gray"
            >
              Back to Bill
            </Button>
            <Button
              leftIcon={<DownloadIcon />}
              colorScheme="blue"
              onClick={() => {
                navigate(`/bill/${id}/preview`);
              }}
            >
              Download Receipt
            </Button>
          </HStack>

          {/* Additional Information */}
          <Card
            bg={cardGradientBg}
            borderColor={cardBorder}
            borderWidth="1px"
          >
            <CardBody>
              <VStack spacing={3} align="start">
                <Text fontWeight="bold" color={textPrimary}>
                  What's Next?
                </Text>
                <Text fontSize="sm" color={textSecondary}>
                  • Your payment has been processed and the bill is now marked as paid
                </Text>
                <Text fontSize="sm" color={textSecondary}>
                  • You will receive a confirmation email shortly with your receipt
                </Text>
                <Text fontSize="sm" color={textSecondary}>
                  • You can download a PDF copy of your receipt at any time from the bill details page
                </Text>
                <Text fontSize="sm" color={textSecondary}>
                  • If you have any questions, please contact our support team
                </Text>
              </VStack>
            </CardBody>
          </Card>
        <Box opacity={0.6}>
          <PoweredBy />
        </Box>
      </VStack>
  );
};

export default BillPaymentSuccess;